import { useStore } from '@nanostores/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from '~/components/ui/Button';
import { updateSubscription } from '~/lib/.client/api/organizations';
import { teamStore } from '~/lib/stores/team';
import { userStore } from '~/lib/stores/user';
import type { OrganizationMembership, OrganizationData } from '~/types/organization';
import { teamsPricingList } from '~/utils/pricing';
import { withSpinner } from '~/utils/withSpinner';

interface PendingDowngradeMenuProps {
  member: OrganizationMembership;
  organization: OrganizationData;
  refetchMembers: VoidFunction;
}

export const PendingDowngradeMenu = ({ member, organization, refetchMembers }: PendingDowngradeMenuProps) => {
  const [open, setOpen] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const currentMembers = useStore(teamStore.members);
  const user = useStore(userStore.user);

  const toggleOpen = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const subscription = organization.billing?.subscription;
  const currentTier = organization.billing?.tier;
  const currentPlan = teamsPricingList.find((plan) => plan.level === `teams_${currentTier}`);

  const handleCancelDowngrade = async () => {
    if (!subscription || !currentPlan) {
      toast.error('Unexpected error occurred. Please, reload the page and try again.');
      return;
    }

    await withSpinner({
      action: async () => {
        try {
          teamStore.members.set(
            currentMembers.map((m) => ({ ...m, downgraded: m.id === member.id ? false : m.downgraded })),
          );
          await updateSubscription({
            organization,
            interval: subscription.interval,
            level: currentPlan.level,
            invitees: [
              ...currentMembers
                .filter((member) => !!member.withMembership && !member.downgraded)
                .map((member) => member.user.providerId),
              member.user.providerId,
            ],
          });
          toast.success('The downgrade was canceled');
          refetchMembers();
        } catch (error) {
          if (error instanceof Error && error.message) {
            toast.error(error.message);
          }
        } finally {
          setOpen(false);
        }
      },
      onStateUpdate: setIsCanceling,
    });
  };

  if (!user?.featureFlags.teamManagement) {
    return (
      <div className="bg-bolt-ds-warning-container rounded-md text-xs text-bolt-ds-warning py-1.5 px-3">
        Pending downgrade
      </div>
    );
  }

  return (
    <DropdownMenu.Root open={open} onOpenChange={toggleOpen}>
      <DropdownMenu.Trigger asChild>
        <Button
          variant="link-color"
          color="warning"
          className="bg-bolt-ds-warning-container text-xs text-bolt-ds-warning p-1.5"
        >
          Pending downgrade
        </Button>
      </DropdownMenu.Trigger>

      {open && (
        <DropdownMenu.Portal forceMount>
          <DropdownMenu.Content
            className="z-max "
            asChild
            forceMount
            sideOffset={4}
            onFocusOutside={() => setOpen(false)}
          >
            <div className="bg-bolt-ds-surfaceOne p-1 border border-bolt-elements-borderColor rounded-md text-sm flex flex-col gap-1">
              <p className="p-2 text-xs text-bolt-ds-text-tertiary">Pending downgrade on next refill cycle.</p>
              <Button
                disabled={isCanceling}
                loading={isCanceling}
                size="md"
                className="text-bolt-ds-danger-highlight hover:text-bolt-ds-danger w-full bg-bolt-ds-surfaceTwo hover:bg-bolt-ds-surfaceTwo"
                onClick={() => handleCancelDowngrade()}
              >
                Cancel downgrade
              </Button>
            </div>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      )}
    </DropdownMenu.Root>
  );
};
